/**
 * SESSIONEN — vem som är inloggad, i vilken firma, och med vilken roll.
 *
 * ROLLEN KOMMER UR ME-SVARET, ALDRIG UR LICENSBEVISET.
 * LicenseClaims bär både role och subscription_status, men de är klientens
 * OFFLINEKOPIA (beslut 11 mot 12). Servern slår upp båda i D1 vid VARJE anrop
 * med auth 'company', även läsande, och DEN rollen avgör
 * (worker/src/auth/authenticate.ts). En avskedad montör, en degraderad chef
 * eller en borttagen bokförare får FORBIDDEN på nästa anrop oavsett vad hans
 * bevis påstår. Klienten läser därför CompanySummary.role ur ME/AUTH_VERIFY,
 * som är samma D1-uppslag.
 *
 * FYRA TILLSTÅND, och 'inloggad' GARANTERAR EN AKTIV FIRMA. Ett konto utan
 * firma är ett riktigt läge (kontot skapas av inloggningsbegäran, firman av
 * CREATE_COMPANY) och har fått ett eget tillstånd i stället för ett
 * `aktiv: CompanySummary | null` som varje skärm hade behövt pröva. Inne i
 * appen är firman alltid där.
 *
 * FEM HÄRLEDDA SVAR som skärmarna ställer om och om igen. De räknas fram HÄR,
 * på ETT ställe, ur kontraktets egna data:
 *   vy           ROLE_VIEW[roll] — 'full' | 'worker' | 'guest' (beslut 16, 17)
 *   kanSeLaget   vy === 'full', dvs. manager ELLER bookkeeper
 *   arChef       roll === 'manager' — den som får SKRIVA stamdata och attestera
 *   arAgare      is_owner — de tre rutterna med requires_owner (beslut 14)
 *   arLast       subscription_status === 'locked' (beslut 11)
 *
 * kanSeLaget OCH arChef ÄR INTE SAMMA SAK, och det är med flit. Bokföraren står
 * i FULL_VIEW_ROLES och når TODAY, INVOICE_BASIS, SUBSCRIPTION och
 * BILLING_PERIODS — han SER hela laget. Men han förekommer inte i EN ENDA rad
 * i SYNC_RULES (beslut 07), så han kan inte attestera, inte låsa en vecka och
 * inte röra en rad. Lagets skärmar ska därför synas för honom och deras
 * KNAPPAR vara borta. Använd kanSeLaget för att visa, arChef för att låta göra.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ME } from '@delat/routes';
import { ROLE_VIEW } from '@delat/types';
import type { AccountRow, ClientView, CompanyId, CompanySummary, Role } from '@delat/types';
import { klienten, senasteUtloggningsfel, valvet } from '../api';
import type { Klartidfel } from '../api';
import { granskaMe } from '../api/granskare/konto';
import { glomAllaFirmaminnen } from './firmaminnen';

const FIRMANYCKEL = 'klartid.firma.v1';

export interface Inloggad {
  readonly status: 'inloggad';
  readonly konto: AccountRow;
  readonly firmor: readonly CompanySummary[];
  readonly aktiv: CompanySummary;
  readonly firmaId: CompanyId;
  readonly roll: Role;
  readonly vy: ClientView;
  readonly kanSeLaget: boolean;
  readonly arChef: boolean;
  readonly arAgare: boolean;
  readonly arLast: boolean;
}

export type Session =
  | { readonly status: 'laddar' }
  | { readonly status: 'utloggad'; readonly skal: Klartidfel | null }
  | { readonly status: 'ingen_firma'; readonly konto: AccountRow }
  | Inloggad;

export interface Sessionsstyrning {
  readonly session: Session;
  /** Hämtar ME på nytt. Efter en rolländring, en ny firma eller en omloggning. */
  readonly laddaOm: () => void;
  readonly bytFirma: (id: CompanyId) => void;
  /**
   * FIRMAN FINNS INTE LÄNGRE (DELETE_COMPANY). Licensen bort ur valvet,
   * modulminnena tömda, och sessionen flyttad I SAMMA ANDETAG: till en annan
   * firma om kontot har en, annars till 'ingen_firma'. Synkront med flit — ett
   * enda anrop mot den raderade firman efter att licensen glömts hade gett
   * UNAUTHENTICATED i klienten, och den rensar HELA valvet. Se RaderaFirma.tsx.
   */
  readonly lamnaFirma: (id: CompanyId) => void;
  /** Rensar valvet. Enhetens id lämnas kvar — det är telefonens, inte kontots. */
  readonly loggaUt: () => void;
}

const SessionsSammanhang = createContext<Sessionsstyrning | null>(null);

const lasSparadFirma = (): string | null => {
  try {
    return globalThis.localStorage?.getItem(FIRMANYCKEL) ?? null;
  } catch {
    return null;
  }
};

const skrivSparadFirma = (id: string | null): void => {
  try {
    if (id === null) globalThis.localStorage?.removeItem(FIRMANYCKEL);
    else globalThis.localStorage?.setItem(FIRMANYCKEL, id);
  } catch {
    /* Utan lagring väljs första firman vid varje start. Inte värre. */
  }
};

/**
 * Vilken firma som är aktiv. Den sparade om den finns kvar bland kontots
 * firmor, annars den första. En sparad firma man INTE längre är medlem i får
 * inte väljas — då skulle varje anrop svara FORBIDDEN utan att någon förstod
 * varför.
 */
const valjFirma = (
  firmor: readonly CompanySummary[],
  onskad: string | null,
): CompanySummary | null =>
  firmor.find((f) => f.id === onskad) ?? firmor[0] ?? null;

/**
 * De fem härledda svaren (se filhuvudet), räknade på ETT ställe. Både
 * ME-effekten och lamnaFirma bygger sin session här, så att de aldrig kan
 * härleda kanSeLaget eller arAgare olika.
 */
const inloggadAv = (
  konto: AccountRow,
  firmor: readonly CompanySummary[],
  aktiv: CompanySummary,
): Inloggad => {
  const vy = ROLE_VIEW[aktiv.role];
  return {
    status: 'inloggad',
    konto,
    firmor,
    aktiv,
    firmaId: aktiv.id,
    roll: aktiv.role,
    vy,
    kanSeLaget: vy === 'full',
    arChef: aktiv.role === 'manager',
    arAgare: aktiv.is_owner,
    arLast: aktiv.subscription_status === 'locked',
  };
};

export const Sessionsvard = ({ children }: { readonly children: ReactNode }) => {
  const [session, sattSession] = useState<Session>({ status: 'laddar' });
  const [onskadFirma, sattOnskadFirma] = useState<string | null>(lasSparadFirma);
  const [omgang, sattOmgang] = useState(0);

  const laddaOm = useCallback(() => sattOmgang((n) => n + 1), []);

  const loggaUt = useCallback(() => {
    valvet.rensa();
    // MODULMINNENA MÅSTE MED. Datalagren håller hela firmans data i
    // modulvariabler i 15-30 sekunder och appen laddas aldrig om, så en
    // utloggning som bara rensade valvet lämnade kvar föregående användares
    // tidrader, arbetsordrar och PRISER i minnet — och nästa inloggning i samma
    // flik fick dem serverade utan ett enda anrop. Se app/firmaminne.ts.
    glomAllaFirmaminnen();
    skrivSparadFirma(null);
    sattOnskadFirma(null);
    sattSession({ status: 'utloggad', skal: null });
  }, []);

  const bytFirma = useCallback((id: CompanyId) => {
    // Cacherna är nycklade på firmaId, så bytet är egentligen redan säkert.
    // Raden står här ändå: ett lager som glömmer nyckeln ska inte kunna visa
    // fel firmas siffror.
    glomAllaFirmaminnen();
    skrivSparadFirma(id);
    sattOnskadFirma(id);
  }, []);

  const lamnaFirma = useCallback(
    (id: CompanyId) => {
      // LICENSEN FÖRST. Den är det enda beviset som pekar på firman, och ett
      // bevis för en firma som inte finns är FORBIDDEN på varje anrop.
      valvet.glomLicens(id);
      glomAllaFirmaminnen();
      if (session.status !== 'inloggad') return;

      const kvar = session.firmor.filter((f) => f.id !== id);
      const nasta = kvar[0] ?? null;
      if (nasta === null) {
        // Kontot finns, firman inte. Samma värld som efter en inloggning
        // utan firma — och NyFirma står där, så vägen vidare är bara ett
        // formulär bort. Sessionen sätts direkt; att onskadFirma byts gör
        // dessutom att ME hämtas om i bakgrunden och säger samma sak.
        skrivSparadFirma(null);
        sattOnskadFirma(null);
        sattSession({ status: 'ingen_firma', konto: session.konto });
        return;
      }
      // SYNKRONT BYTE, sedan färsk ME. Sessionen pekar på nästa firma redan i
      // den här renderingen, så ingen skärm hinner fråga den raderade om
      // något. Omhämtningen efteråt ger listan som servern ser den.
      skrivSparadFirma(nasta.id);
      sattOnskadFirma(nasta.id);
      sattSession(inloggadAv(session.konto, kvar, nasta));
      sattOmgang((n) => n + 1);
    },
    [session],
  );

  useEffect(() => {
    const konto = valvet.las().konto;
    if (konto === null) {
      sattSession({ status: 'utloggad', skal: senasteUtloggningsfel() });
      return;
    }

    const styrning = new AbortController();
    let levande = true;

    void (async () => {
      const svar = await klienten.anrop(ME, { signal: styrning.signal }, granskaMe);
      if (!levande) return;
      if (!svar.ok) {
        if (svar.fel.slag === 'nat' && svar.fel.avbrutet) return;
        // Klienten har redan rensat valvet vid UNAUTHENTICATED/LICENSE_EXPIRED.
        // Övriga fel (nätet, servern) ska INTE logga ut någon — men utan ME vet
        // vi inte vem det är, så appen får ändå visa inloggningen med skälet.
        sattSession({ status: 'utloggad', skal: svar.fel });
        return;
      }
      const me = svar.varde;
      const aktiv = valjFirma(me.companies, onskadFirma);
      if (aktiv === null) {
        sattSession({ status: 'ingen_firma', konto: me.account });
        return;
      }
      if (aktiv.id !== onskadFirma) skrivSparadFirma(aktiv.id);
      sattSession(inloggadAv(me.account, me.companies, aktiv));
    })();

    return () => {
      levande = false;
      styrning.abort();
    };
  }, [omgang, onskadFirma]);

  // Rensas valvet någon annanstans ifrån — klienten vid LICENSE_EXPIRED, eller
  // en annan flik — ska den här fliken följa med ut.
  useEffect(
    () =>
      valvet.prenumerera(() => {
        if (valvet.las().konto === null) {
          sattSession({ status: 'utloggad', skal: senasteUtloggningsfel() });
        }
      }),
    [],
  );

  const varde = useMemo<Sessionsstyrning>(
    () => ({ session, laddaOm, bytFirma, lamnaFirma, loggaUt }),
    [session, laddaOm, bytFirma, lamnaFirma, loggaUt],
  );

  return <SessionsSammanhang value={varde}>{children}</SessionsSammanhang>;
};

export const useSession = (): Sessionsstyrning => {
  const varde = useContext(SessionsSammanhang);
  if (varde === null) throw new Error('useSession användes utanför <Sessionsvard>');
  return varde;
};

/**
 * DEN INLOGGADE SESSIONEN, garanterat med en aktiv firma.
 *
 * Använd den här i varje skärm. Den kastar om appen inte är inloggad — vilket
 * inte kan hända, för skärmarna renderas bara innanför <Appen>, som visar
 * inloggningen i de övriga tillstånden. Kastet finns för att typen ska bli
 * `Inloggad` och inte `Session`, så att `session.firmaId` går att skicka rakt
 * in i ett anrop utan att varje skärm prövar samma tre fall.
 */
export const useInloggad = (): Inloggad => {
  const { session } = useSession();
  if (session.status !== 'inloggad') {
    throw new Error('useInloggad användes utanför en inloggad session');
  }
  return session;
};
